import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Search, X } from 'lucide-react';
import {
    format,
    startOfMonth,
    endOfMonth,
    startOfWeek,
    endOfWeek,
    eachDayOfInterval,
    isSameMonth,
    isSameDay,
    addMonths,
    subMonths,
    addWeeks,
    subWeeks,
    addDays,
    subDays
} from 'date-fns';
import api from '../api';
import SEO from '../components/SEO';

export default function CalendarPage() {
    // State Management
    const [events, setEvents] = useState([]);
    const [filteredEvents, setFilteredEvents] = useState([]);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [view, setView] = useState('month'); // month, week, day
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [loading, setLoading] = useState(true);

    // Filter & Search State
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [categories, setCategories] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState(null);

    // Load events and categories on mount
    useEffect(() => {
        loadEvents();
        loadCategories();
    }, []);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyPress = (e) => {
            // Ignore if user is typing in an input
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

            switch (e.key) {
                case 'ArrowLeft':
                    e.preventDefault();
                    navigatePrev();
                    break;
                case 'ArrowRight':
                    e.preventDefault();
                    navigateNext();
                    break;
                case 't':
                case 'T':
                    e.preventDefault();
                    goToToday();
                    break;
                case 'm':
                case 'M':
                    e.preventDefault();
                    setView('month');
                    break;
                case 'w':
                case 'W':
                    e.preventDefault();
                    setView('week');
                    break;
                case 'd':
                case 'D':
                    e.preventDefault();
                    setView('day');
                    break;
                case 'Escape':
                    setSelectedEvent(null);
                    break;
                default:
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [currentDate, view]);

    // Filter events when search or category changes
    useEffect(() => {
        filterEvents();
    }, [events, searchQuery, selectedCategory]);

    const loadEvents = async () => {
        try {
            const data = await api.getEvents({ limit: 100 });
            setEvents(data);
        } catch (error) {
            console.error('Error loading events:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadCategories = async () => {
        try {
            const data = await api.getCategories();
            setCategories(data);
        } catch (error) {
            console.error('Error loading categories:', error);
        }
    };

    const filterEvents = () => {
        let filtered = [...events];

        // Filter by search query
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(event =>
                event.title.toLowerCase().includes(query) ||
                (event.location && event.location.toLowerCase().includes(query)) ||
                (event.description && event.description.toLowerCase().includes(query))
            );
        }

        // Filter by category
        if (selectedCategory !== 'all') {
            filtered = filtered.filter(event => event.category === selectedCategory);
        }

        setFilteredEvents(filtered);
    };

    const getEventsForDay = (date) => {
        return filteredEvents.filter(event =>
            isSameDay(new Date(event.date), date)
        );
    };

    // Navigation Logic
    const navigatePrev = () => {
        if (view === 'month') {
            setCurrentDate(subMonths(currentDate, 1));
        } else if (view === 'week') {
            const newDate = subWeeks(currentDate, 1);
            setCurrentDate(newDate);
            setSelectedDate(newDate);
        } else {
            const newDate = subDays(currentDate, 1);
            setCurrentDate(newDate);
            setSelectedDate(newDate);
        }
    };

    const navigateNext = () => {
        if (view === 'month') {
            setCurrentDate(addMonths(currentDate, 1));
        } else if (view === 'week') {
            const newDate = addWeeks(currentDate, 1);
            setCurrentDate(newDate);
            setSelectedDate(newDate);
        } else {
            const newDate = addDays(currentDate, 1);
            setCurrentDate(newDate);
            setSelectedDate(newDate);
        }
    };

    const goToToday = () => {
        const today = new Date();
        setCurrentDate(today);
        setSelectedDate(today);
    };

    // Update currentDate if selectedDate changes and not in current range
    useEffect(() => {
        if (!isSameMonth(selectedDate, currentDate) && view === 'month') {
            setCurrentDate(selectedDate);
        }
    }, [selectedDate, view]);

    // Month View - Clean Design with Event Cards
    const renderMonthView = () => {
        const monthStart = startOfMonth(currentDate);
        const monthEnd = endOfMonth(currentDate);
        const calendarStart = startOfWeek(monthStart);
        const calendarEnd = endOfWeek(monthEnd);
        const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

        return (
            <div className="new-month-view">
                {/* Day Headers */}
                <div className="month-day-headers">
                    {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map(day => (
                        <div key={day} className="month-day-header">{day}</div>
                    ))}
                </div>

                {/* Calendar Grid */}
                <div className="month-calendar-grid">
                    {days.map((day, idx) => {
                        const dayEvents = getEventsForDay(day);
                        const isToday = isSameDay(day, new Date());
                        const isCurrentMonth = isSameMonth(day, currentDate);

                        return (
                            <div
                                key={idx}
                                className={`month-day-cell ${isToday ? 'is-today' : ''} ${!isCurrentMonth ? 'other-month' : ''}`}
                                role="button"
                                tabIndex={0}
                                aria-label={`${format(day, 'MMMM d, yyyy')} - ${dayEvents.length} event${dayEvents.length !== 1 ? 's' : ''}`}
                            >
                                <div className="month-day-number">
                                    {format(day, 'd')}
                                </div>

                                {/* Event Cards */}
                                {dayEvents.length > 0 && (
                                    <div className="month-event-cards">
                                        {dayEvents.slice(0, 2).map((event) => (
                                            <Link
                                                key={event.id}
                                                to={`/events/${event.id}`}
                                                className="month-event-card"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {event.image_url && (
                                                    <div className="month-event-image">
                                                        <img src={event.image_url} alt={event.title} onError={(e) => { e.target.onerror = null; e.target.src = 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=800'; }} />
                                                    </div>
                                                )}
                                                <div className="month-event-info">
                                                    <span className="month-event-time">{format(new Date(event.date), 'h:mm a')}</span>
                                                    <h5 className="month-event-title">{event.title}</h5>
                                                </div>
                                            </Link>
                                        ))}
                                        {dayEvents.length > 2 && (
                                            <div className="month-more-events">
                                                +{dayEvents.length - 2} more
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    // Week View - Clean Design
    const renderWeekView = () => {
        const weekStart = startOfWeek(currentDate);
        const days = eachDayOfInterval({
            start: weekStart,
            end: addDays(weekStart, 6)
        });

        const selectedDayEvents = getEventsForDay(selectedDate);

        return (
            <div className="new-week-view">
                {/* Week Date Selector */}
                <div className="week-date-selector-row">
                    {days.map((day, idx) => {
                        const isSelected = isSameDay(day, selectedDate);
                        const isToday = isSameDay(day, new Date());
                        const dayEvents = getEventsForDay(day);

                        return (
                            <button
                                key={idx}
                                className={`week-day-button ${isSelected ? 'selected' : ''} ${isToday ? 'is-today' : ''}`}
                                onClick={() => setSelectedDate(day)}
                                aria-label={`${format(day, 'EEEE, MMMM d')} - ${dayEvents.length} events`}
                            >
                                <span className="week-day-label">{format(day, 'EEE').toUpperCase()}</span>
                                <span className="week-day-number">{format(day, 'd')}</span>
                                {dayEvents.length > 0 && <span className="week-event-indicator" />}
                            </button>
                        );
                    })}
                </div>

                {/* Today Section */}
                <div className="week-events-section">
                    <h2 className="week-section-title">
                        {isSameDay(selectedDate, new Date()) ? 'Today' : format(selectedDate, 'EEEE')}
                    </h2>

                    {selectedDayEvents.length > 0 ? (
                        <div className="week-event-list">
                            {selectedDayEvents.map((event) => (
                                <Link key={event.id} to={`/events/${event.id}`} className="week-event-card">
                                    {event.image_url && (
                                        <div className="week-event-image">
                                            <img src={event.image_url} alt={event.title} onError={(e) => { e.target.onerror = null; e.target.src = 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=800'; }} />
                                        </div>
                                    )}
                                    <div className="week-event-details">
                                        <span className="week-event-time">{format(new Date(event.date), 'h:mm a')}</span>
                                        <h4 className="week-event-title">{event.title}</h4>
                                        {event.location && <span className="week-event-location">{event.location}</span>}
                                    </div>
                                </Link>
                            ))}
                        </div>
                    ) : (
                        <div className="week-empty-state">
                            <p>No events scheduled for this day.</p>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Day View - Clean Design
    const renderDayView = () => {
        const dayEvents = getEventsForDay(selectedDate);
        const isToday = isSameDay(selectedDate, new Date());

        return (
            <div className="new-day-view">
                {/* Day Header */}
                <div className="day-view-header">
                    <h2 className="day-view-day-name">
                        {format(selectedDate, 'EEEE')}
                    </h2>
                    <p className="day-view-date">
                        {format(selectedDate, 'MMMM d, yyyy')}
                    </p>
                    {isToday && <span className="day-today-badge">{format(selectedDate, 'd')}</span>}
                </div>

                {/* Events List */}
                {dayEvents.length > 0 ? (
                    <div className="day-event-list">
                        {dayEvents.map((event) => (
                            <Link key={event.id} to={`/events/${event.id}`} className="day-event-card">
                                {event.image_url && (
                                    <div className="day-event-image">
                                        <img src={event.image_url} alt={event.title} onError={(e) => { e.target.onerror = null; e.target.src = 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=800'; }} />
                                    </div>
                                )}
                                <div className="day-event-details">
                                    <span className="day-event-time">{format(new Date(event.date), 'h:mm a')}</span>
                                    <h4 className="day-event-title">{event.title}</h4>
                                    {event.location && <span className="day-event-location">{event.location}</span>}
                                </div>
                            </Link>
                        ))}
                    </div>
                ) : (
                    <div className="day-empty-state">
                        <p>No events scheduled for this day.</p>
                    </div>
                )}
            </div>
        );
    };

    return (
        <main className="main">
            <SEO
                title="Calendar"
                description="View all Indian Heritage and Arts events in Singapore on our interactive calendar. Plan your cultural experiences with our comprehensive event schedule."
                keywords="event calendar Singapore, Indian events calendar, cultural calendar, arts events schedule, Singapore heritage calendar"
            />
            {/* Gradient Banner at Top */}
            <div style={{
                background: 'linear-gradient(135deg, rgba(222, 65, 176, 0.9) 0%, rgba(254, 205, 109, 0.9) 50%, rgba(255, 252, 242, 0.9) 100%)',
                height: window.innerWidth <= 768 ? '20px' : '120px',
                marginBottom: '32px'
            }} />

            <div className="container calendar-page-container">
                <div className="calendar-container">
                    <div className="calendar-header">
                        <div className="header-top">
                            <h1 className="calendar-title">
                                {view === 'day'
                                    ? format(currentDate, 'MMMM d, yyyy')
                                    : format(currentDate, 'MMMM yyyy')
                                }
                            </h1>
                            <button
                                className="btn btn-secondary today-btn"
                                onClick={goToToday}
                            >
                                Today
                            </button>
                        </div>

                        {/* Search and Filter Section */}
                        <div className="calendar-filters">
                            <div className="search-input-wrapper">
                                <Search size={18} className="search-icon" />
                                <input
                                    type="text"
                                    placeholder="Search events..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="calendar-search-input"
                                    aria-label="Search events"
                                />
                            </div>
                            {/* Category filter hidden as requested */}
                            {(searchQuery || selectedCategory !== 'all') && (
                                <button
                                    className="btn btn-ghost calendar-reset-btn"
                                    onClick={() => {
                                        setSearchQuery('');
                                        setSelectedCategory('all');
                                    }}
                                    aria-label="Clear filters"
                                >
                                    Clear
                                </button>
                            )}
                        </div>

                        <div className="header-bottom">
                            <div className="calendar-views">
                                {['month', 'week', 'day'].map((v) => (
                                    <button
                                        key={v}
                                        className={`calendar-view-btn ${view === v ? 'active' : ''}`}
                                        onClick={() => setView(v)}
                                        aria-label={`${v.charAt(0).toUpperCase() + v.slice(1)} view`}
                                        aria-pressed={view === v}
                                    >
                                        {v.charAt(0).toUpperCase() + v.slice(1)}
                                    </button>
                                ))}
                            </div>

                            <div className="calendar-nav">
                                <button
                                    onClick={navigatePrev}
                                    className="nav-btn"
                                    aria-label={`Previous ${view}`}
                                >
                                    <ChevronLeft size={20} />
                                </button>
                                <button
                                    onClick={navigateNext}
                                    className="nav-btn"
                                    aria-label={`Next ${view}`}
                                >
                                    <ChevronRight size={20} />
                                </button>
                            </div>
                        </div>
                    </div>
                    {loading ? (
                        <div className="skeleton-calendar" />
                    ) : (
                        <>
                            {(searchQuery || selectedCategory !== 'all') && (
                                <div className="calendar-results-info">
                                    <span className="results-count">
                                        {filteredEvents.length} event{filteredEvents.length !== 1 ? 's' : ''} found
                                    </span>
                                </div>
                            )}
                            <div className="calendar-content">
                                {view === 'month' && renderMonthView()}
                                {view === 'week' && renderWeekView()}
                                {view === 'day' && renderDayView()}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </main>
    );
}
